'use client';
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import { useCombobox, useMultipleSelection } from 'downshift';
import useSWR from 'swr';
import { Icon } from '@ui/icons';
import { Chip } from '../Chip';
import { ErrorText } from '../ErrorText';
import {
  SearchContainer,
  SelectedItemWrapper,
  ControlRow,
  SearchInput,
  OpenButton,
  InputContainer,
  Menu,
  InputSizer,
  SearchLabel,
} from './styles';
import { MenuContent } from './MenuContent';

const removeWhitespace = searchInput => {
  const formattedInput = searchInput.trim();
  return formattedInput;
};

export const MultiSelect = ({
  searchLabel,
  name,
  values,
  selectedValues,
  // if there are featured values you need to filter out because you're using
  // another selection type (ie: AllergiesForm on Onboarding), please pass in
  // these featuredValues as an array. otherwise, you will receive all searchItems
  featuredValues = [],
  onChange,
  onFocus,
  onBlur,
  max,
  itemsToShow = {
    desktop: 6,
    mobile: 4,
  },
  backgroundColor = 'background.lightest',
  error = null,
  onboardingBorders = false,
  borderColor,
  chevronColor = 'text.tertiary',
  debounceMs = 300,
  showTopOfNextItem = false,
}) => {
  const { getSelectedItemProps, getDropdownProps, selectedItems } =
    useMultipleSelection({ selectedItems: selectedValues });

  const fetcher = useCallback(
    ([, term]) =>
      Array.isArray(values)
        ? values.filter(({ label }) => label.includes(term))
        : values(term),
    [values],
  );

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  // Only function sources hit the network; array sources filter in-memory,
  // so debouncing is limited to the function case to avoid adding perceptible
  // lag to instant filtering.
  const isFunctionSource = typeof values === 'function';

  const debouncedSetSearch = useMemo(
    () => debounce(setDebouncedSearchValue, debounceMs),
    [debounceMs],
  );

  // Cancel any pending debounced update on unmount.
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const updateSearchValue = useCallback(
    value => {
      setSearchValue(value);
      // An empty term returns the default/featured list — surface it
      // immediately rather than making the user wait out the debounce.
      if (value === '') {
        debouncedSetSearch.cancel();
        setDebouncedSearchValue('');
      } else {
        debouncedSetSearch(value);
      }
    },
    [debouncedSetSearch],
  );

  const resetSearchValue = useCallback(() => {
    debouncedSetSearch.cancel();
    setSearchValue('');
    setDebouncedSearchValue('');
  }, [debouncedSetSearch]);

  // Keep the input responsive while only the debounced term drives fetches.
  const searchTerm = isFunctionSource ? debouncedSearchValue : searchValue;

  const isDebouncePending =
    isFunctionSource &&
    removeWhitespace(searchValue) !== removeWhitespace(searchTerm);

  //prevents text disappearing on allergy form when only featured values are selected
  const onlyFeaturedItemsSelected = selectedValues?.every(sv =>
    featuredValues.some(fv => fv.value === sv.value),
  );

  const hideSearchLabel =
    (selectedItems.length > 0 && !onlyFeaturedItemsSelected) ||
    searchValue !== '';

  const { data: searchItems = [], isValidating } = useSWR(
    [`MultiSelect:${name}`, removeWhitespace(searchTerm)],
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const findCurrentValues = (currentValues, resultValue) => {
    return currentValues.find(currentValue => {
      if (currentValue.label === resultValue) return true;
      return false;
    });
  };

  const getFilteredItems = (items, currentValues) => {
    return items.filter(({ label }) => {
      if (!findCurrentValues(currentValues, label)) return true;
      return false;
    });
  };

  const isMenuLoading = isValidating || isDebouncePending;

  const menuItems = isMenuLoading
    ? []
    : getFilteredItems(searchItems, selectedItems);

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectItem,
  } = useCombobox({
    id: name,
    inputValue: searchValue,
    items: menuItems,
    itemToString: item => item?.label ?? '',
    selectedItem: null,
    onStateChange: ({ type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem) {
            resetSearchValue();
            onChange([...selectedItems, selectedItem]);
            selectItem(null);
          }
          break;
        default:
          break;
      }
    },
  });

  const requestedItemsToShow =
    typeof itemsToShow === 'number'
      ? { mobile: itemsToShow, desktop: itemsToShow }
      : itemsToShow;

  const getMinValue = ({ mobile, desktop }) => {
    return {
      mobile: Math.min(mobile, menuItems.length) || 1,
      desktop: Math.min(desktop, menuItems.length) || 1,
    };
  };

  const itemsToShowValues =
    isMenuLoading || selectedItems.length >= max?.count
      ? {
          mobile: 1,
          desktop: 1,
        }
      : getMinValue(requestedItemsToShow);

  const containerRef = useRef(null);

  const handleBlur = useCallback(
    event => {
      requestAnimationFrame(() => {
        if (!containerRef.current?.contains(document.activeElement)) {
          setInputFocused(false);
          onBlur?.(event);
        }
      });
    },
    [onBlur],
  );

  const collapseEmptyInput =
    selectedItems.length > 0 && searchValue === '' && !inputFocused;

  return (
    <>
      <SearchContainer
        ref={containerRef}
        $backgroundColor={backgroundColor}
        $hasError={Boolean(error)}
        $hasSelection={selectedItems.length > 0}
        $isOpen={isOpen}
        $onboardingBorders={onboardingBorders}
        $borderColor={borderColor}
      >
        <ControlRow>
          {getFilteredItems(selectedItems, featuredValues).map(
            (selectedItem, index) => (
              <SelectedItemWrapper
                key={selectedItem.value}
                {...getSelectedItemProps({ selectedItem, index })}
              >
                <Chip
                  label={selectedItem.label}
                  size="small"
                  tag
                  showIcon="delete"
                  onClick={() =>
                    onChange(
                      selectedItems.filter(item => item !== selectedItem),
                    )
                  }
                />
              </SelectedItemWrapper>
            ),
          )}
          <InputContainer $collapseEmpty={collapseEmptyInput}>
            <SearchInput
              {...getInputProps(
                getDropdownProps({
                  preventKeyAction: isOpen,
                  onKeyDown: event => {
                    if (selectedItems.length === 0 && event.key === 'Enter') {
                      event.preventDefault();
                    }
                  },
                  onChange: e => {
                    updateSearchValue(e.target.value);
                  },
                  onFocus: event => {
                    setInputFocused(true);
                    onFocus?.(event);
                  },
                  onBlur: handleBlur,
                }),
              )}
            />
            <SearchLabel
              {...getLabelProps()}
              $hideSearchLabel={hideSearchLabel}
            >
              {searchLabel}
            </SearchLabel>
            <InputSizer>{searchValue}&nbsp;</InputSizer>
          </InputContainer>
        </ControlRow>
        <OpenButton
          {...getToggleButtonProps()}
          type="button"
          aria-label="toggle menu"
        >
          <Icon
            iconName="chevron-down"
            alt="Open Menu"
            size={1}
            color={chevronColor}
            id="multiselect_open"
          />
        </OpenButton>
        <Menu
          {...getMenuProps()}
          $isOpen={isOpen}
          $itemsToShow={itemsToShowValues}
          $onboardingBorders={onboardingBorders}
          $showTopOfNextItem={showTopOfNextItem}
        >
          {isOpen && (
            <MenuContent
              items={menuItems}
              selectedItems={selectedItems}
              getItemProps={getItemProps}
              highlightedIndex={highlightedIndex}
              searchLabel={searchLabel}
              searchValue={searchValue}
              max={max}
              isLoading={isMenuLoading}
            />
          )}
        </Menu>
      </SearchContainer>
      {error && <ErrorText $centered>{error}</ErrorText>}
    </>
  );
};

const MultiSelectValue = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

MultiSelect.propTypes = {
  searchLabel: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  values: PropTypes.oneOfType([
    PropTypes.arrayOf(MultiSelectValue),
    PropTypes.func,
  ]).isRequired,
  selectedValues: PropTypes.arrayOf(MultiSelectValue).isRequired,
  featuredValues: PropTypes.array,
  max: PropTypes.shape({
    count: PropTypes.number.isRequired,
    message: PropTypes.string.isRequired,
  }),
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  itemsToShow: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      desktop: PropTypes.number.isRequired,
      mobile: PropTypes.number.isRequired,
    }),
  ]),
  backgroundColor: PropTypes.string,
  error: PropTypes.string,
  onboardingBorders: PropTypes.bool,
  debounceMs: PropTypes.number,
  chevronColor: PropTypes.string,
  borderColor: PropTypes.string,
  showTopOfNextItem: PropTypes.bool,
};
